import {
  Stack,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { Search, ExpandMore } from '@mui/icons-material';
import { TenantType } from 'customers/tenants-common-view/lib/schema/Tenant.types';
import { useToggleBilling } from '../../TenantCardSummary/Billing/useToggleBilling';
import { TenantFlagToggle } from '../../TenantCardSummary/TenantFlagToggle';
import OtpModal from 'common/Modals/OtpModal';
import { OTPRequestType } from 'constants/URLEnums';
import { useMemo, useState } from 'react';
import { styles } from 'assets/scss/style-templates';
import { FeatureFlags } from './FeatureFlags';
import { stylingObject } from './TenantFlags.styles';

interface TenantFlagsProps {
  tenant: TenantType;
  handleToggleBillingSuccess?: any;
}

export const TenantFlags = (props: TenantFlagsProps) => {
  const { tenant, handleToggleBillingSuccess } = props;
  const { metadata } = tenant;
  const [searchQuery, setSearchQuery] = useState('');
  const [tenantFlagsExpanded, setTenantFlagsExpanded] = useState(false);
  const [featureFlagsExpanded, setFeatureFlagsExpanded] = useState(false);

  const {
    openOTP,
    setOpenOTP,
    step,
    setStep,
    onSubmit,
    setOtpType,
    setToggledFlag,
    flags,
    sendOTPIsLoading,
    toggleBillingIsLoading,
    getTitleAndReason,
  } = useToggleBilling({ tenant, handleToggleBillingSuccess, metadata });

  const handleFlagToggle = (flag) => {
    setToggledFlag([flag?.name, flag?.value]);
    setOtpType(OTPRequestType.TENANT_FLAG_TOGGLE_OTP);
    setOpenOTP(true);
  };

  const tenantFlags = useMemo(
    () => flags?.filter((data) => !!data) || [],
    [flags],
  );

  const filteredFlags = useMemo(() => {
    if (!searchQuery.trim()) {
      return tenantFlags;
    }
    const query = searchQuery.toLowerCase();
    return tenantFlags.filter(
      (flag) =>
        (flag?.displayName || flag?.name || '')
          .toLowerCase()
          .includes(query),
    );
  }, [tenantFlags, searchQuery]);

  return (
    <>
      <Box sx={stylingObject.container}>
        <Stack spacing={2}>
          {/* Tenant Flags Section */}
          <Accordion
            expanded={tenantFlagsExpanded}
            onChange={() => setTenantFlagsExpanded(!tenantFlagsExpanded)}
            disableGutters
            elevation={0}
            sx={stylingObject.accordion}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={stylingObject.accordionSummary}
            >
              <Typography variant="h6" sx={stylingObject.sectionTitle}>
                Tenant Flags
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2} sx={stylingObject.sectionStack}>
                <TextField
                  placeholder="Search tenant flags..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="small"
                  sx={stylingObject.search}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
                {filteredFlags.length === 0 ? (
                  <Box sx={stylingObject.emptyState}>
                    <Typography variant="body1" color="text.secondary">
                      {searchQuery
                        ? 'No flags found matching your search.'
                        : 'No flags available for this tenant.'}
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead sx={stylingObject.tableHead}>
                        <TableRow>
                          <TableCell sx={stylingObject.tableCell}>
                            <Typography sx={stylingObject.sectionTitle}>
                              Flag Name
                            </Typography>
                          </TableCell>
                          <TableCell sx={stylingObject.statusHeaderCell}>
                            <Typography sx={stylingObject.sectionTitle}>
                              Status
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredFlags.map((flag, index) => (
                          <TableRow key={flag?.name || index} hover>
                            <TableCell sx={stylingObject.tableCell}>
                              <Typography variant="body1" sx={stylingObject.rowTitle}>
                                {flag?.displayName || flag?.name}
                              </Typography>
                            </TableCell>
                            <TableCell sx={stylingObject.statusCell}>
                              <TenantFlagToggle
                                checked={flag.value}
                                onChange={() => handleFlagToggle(flag)}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Feature Flags Section */}
          <Accordion
            expanded={featureFlagsExpanded}
            onChange={() => setFeatureFlagsExpanded(!featureFlagsExpanded)}
            disableGutters
            elevation={0}
            sx={stylingObject.accordion}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={stylingObject.accordionSummary}
            >
              <Typography variant="h6" sx={stylingObject.sectionTitle}>
                Feature Flags
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FeatureFlags tenant={tenant} />
            </AccordionDetails>
          </Accordion>
        </Stack>
      </Box>
      <OtpModal
        open={openOTP}
        setOpen={setOpenOTP}
        title={getTitleAndReason()?.title}
        subTitle={getTitleAndReason()?.subTitle}
        onSubmit={(val) => {
          onSubmit(val);
        }}
        isSubmitting={step === 1 ? sendOTPIsLoading : toggleBillingIsLoading}
        otpUser="Ops admin"
        step={step}
        setStep={setStep}
        reasonHeading={getTitleAndReason()?.reasonHeading}
        otpType={getTitleAndReason()?.otpType}
      />
    </>
  );
};
